import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Apple, Smartphone, Share, Plus, CheckCircle2, Compass } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import api, { BACKEND_URL } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { getDeviceType } from "@/lib/device";
import ApkInstaller from "@/components/ApkInstaller";
import { toast } from "sonner";

function PremiumSteps({ steps, label }) {
  return (
    <div className="mt-7" data-testid="install-steps">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[var(--rc-gold)]">{label}</span>
        <span className="h-px flex-1 bg-gradient-to-r from-[var(--rc-gold)]/40 to-transparent" />
      </div>
      <div className="relative">
        {/* vertical connector */}
        <span className="absolute left-[19px] top-2 bottom-2 w-px bg-gradient-to-b from-[var(--rc-gold)]/40 via-[var(--rc-gold)]/20 to-transparent" aria-hidden />
        <div className="space-y-2.5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} data-testid={`install-hub-step-${i}`}
                className="group relative flex items-start gap-4 rounded-2xl border border-[var(--rc-gold)]/12 bg-gradient-to-br from-[var(--rc-surface)] to-[var(--rc-bg)]/40 px-4 py-3.5 transition-colors hover:border-[var(--rc-gold)]/35">
                <div className="relative shrink-0">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--rc-gold)] text-black font-bold text-sm shadow-[0_0_0_4px_var(--rc-bg)]">{i + 1}</span>
                </div>
                <div className="min-w-0 pt-0.5">
                  <div className="flex items-center gap-1.5 font-semibold text-[var(--rc-text)]">
                    {Icon && <Icon size={15} className="text-[var(--rc-gold)] shrink-0" />} {s.t}
                  </div>
                  <p className="mt-1 text-sm text-[var(--rc-muted)] leading-relaxed">{s.d}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const Card = ({ children, testid }) => (
  <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
    data-testid={testid}
    className="relative overflow-hidden rounded-3xl border border-[var(--rc-gold)]/25 bg-[var(--rc-surface)] p-8 sm:p-10">
    <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.10), transparent 60%)" }} />
    <div className="relative">{children}</div>
  </motion.div>
);

export default function InstallHub({ openApp, autoStart = false }) {
  const { lang } = useI18n();
  const fr = lang === "fr";
  const { canInstall, installed, promptInstall } = usePWAInstall();
  const [device, setDevice] = useState("desktop");
  const [apk, setApk] = useState(null); // { available, version, size, name, url }

  useEffect(() => {
    setDevice(getDeviceType());
    api.get("/app/android/latest").then(({ data }) => setApk(data)).catch(() => setApk({ available: false }));
  }, []);

  const apkUrl = `${BACKEND_URL}/api/app/android/apk`;
  const available = !!(apk && apk.available);

  // ---- iOS (PWA only) ----
  const IOSSection = (
    <Card testid="install-ios">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-12 w-12 rounded-2xl grid place-items-center bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)]"><Apple size={24} /></span>
        <div>
          <h3 className="font-heading text-2xl">{fr ? "RoyalChat Nova pour iOS" : "RoyalChat Nova for iOS"}</h3>
          <p className="text-sm text-[var(--rc-muted)]">{fr ? "Installez l'application web (PWA) sur votre iPhone" : "Install the web app (PWA) on your iPhone"}</p>
        </div>
      </div>
      {installed ? (
        <div className="flex items-center gap-2 text-[var(--rc-gold)] py-4"><CheckCircle2 size={20} /> {fr ? "Déjà installée 👑" : "Already installed 👑"}</div>
      ) : (
        <button
          onClick={async () => {
            if (canInstall) { await promptInstall(); }
            else { toast(fr ? "Ouvrez ce site dans Safari, puis Partager → Sur l'écran d'accueil" : "Open this site in Safari, then Share → Add to Home Screen", { icon: "🍏", duration: 6000 }); }
          }}
          data-testid="ios-install-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-4 text-lg hover:bg-[var(--rc-gold-light)] transition-colors">
          <Apple size={18} /> {fr ? "Installer sur iPhone" : "Install on iPhone"}
        </button>
      )}
      <PremiumSteps
        label={fr ? "Installation" : "How to install"}
        steps={fr
          ? [
              { icon: Compass, t: "Ouvrez dans Safari", d: "Ouvrez ce site dans le navigateur Safari sur votre iPhone (pas dans une autre app)." },
              { icon: Share, t: "Touchez Partager", d: "Appuyez sur l'icône Partager en bas de l'écran (le carré avec une flèche vers le haut)." },
              { icon: Plus, t: "Sur l'écran d'accueil", d: "Faites défiler et touchez « Sur l'écran d'accueil », puis Ajouter." },
              { icon: CheckCircle2, t: "Ouvrez RoyalChat Nova", d: "L'icône RoyalChat Nova apparaît sur votre écran d'accueil. Touchez-la pour lancer l'app." },
            ]
          : [
              { icon: Compass, t: "Open in Safari", d: "Open this site in the Safari browser on your iPhone (not another app)." },
              { icon: Share, t: "Tap Share", d: "Tap the Share icon at the bottom of the screen (the square with an up arrow)." },
              { icon: Plus, t: "Add to Home Screen", d: "Scroll and tap \u201cAdd to Home Screen\u201d, then tap Add." },
              { icon: CheckCircle2, t: "Open RoyalChat Nova", d: "The RoyalChat Nova icon appears on your Home Screen. Tap it to launch the app." },
            ]}
      />
    </Card>
  );

  // ---- Android (APK only) ----
  const AndroidSection = (
    <Card testid="install-android">
      <div className="flex items-center gap-3 mb-2">
        <span className="h-12 w-12 rounded-2xl grid place-items-center bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)]"><Smartphone size={24} /></span>
        <div>
          <h3 className="font-heading text-2xl">{fr ? "Application Android native" : "RoyalChat Nova Native Android App"}</h3>
          <p className="text-sm text-[var(--rc-muted)]">{fr ? "Téléchargez l'APK officiel" : "Download the official APK"}</p>
        </div>
      </div>

      {available ? (
        <ApkInstaller
          apkUrl={apkUrl}
          filename={apk.name || `RoyalChat-${apk.version || ""}.apk`}
          version={apk.version}
          size={apk.size}
          autoStart={autoStart}
          fr={fr}
        />
      ) : (
        <div className="mt-5 rounded-2xl border border-[var(--rc-border)] bg-[var(--rc-bg)]/50 px-5 py-4 text-sm text-[var(--rc-muted)]" data-testid="apk-unavailable">
          {fr ? "L'APK sera bientôt disponible. Revenez sous peu." : "The APK will be available soon. Please check back shortly."}
        </div>
      )}
    </Card>
  );

  // ---- Desktop (QR only — scan to open the site on your phone) ----
  const siteUrl = typeof window !== "undefined" ? window.location.origin : "https://royalchat.online";
  const DesktopSection = (
    <Card testid="install-desktop">
      <div className="flex flex-col items-center text-center gap-5">
        <div className="rounded-3xl bg-white p-4 shadow-lg" data-testid="apk-qr">
          <QRCodeSVG value={siteUrl} size={200} level="M" includeMargin={false} />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-2 justify-center">
            <span className="h-10 w-10 rounded-2xl grid place-items-center bg-[var(--rc-gold)]/10 border border-[var(--rc-gold)]/20 text-[var(--rc-gold)]"><Smartphone size={20} /></span>
            <h3 className="font-heading text-2xl">{fr ? "Ouvrez sur votre téléphone" : "Open on your phone"}</h3>
          </div>
          <p className="text-[var(--rc-muted)] leading-relaxed max-w-sm mx-auto">
            {fr
              ? "RoyalChat Nova est fait pour le mobile. Scannez ce QR code avec l'appareil photo de votre téléphone pour ouvrir le site et l'installer."
              : "RoyalChat Nova is built for mobile. Scan this QR code with your phone's camera to open the site and install it."}
          </p>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="install-hub">
      {device === "ios" && IOSSection}
      {device === "android" && AndroidSection}
      {device === "desktop" && DesktopSection}
    </div>
  );
}
